import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { fetchIdibuIframeHtml, fetchIdibuStatus } from "../../Redux/ActionCreators/idibuAction";
import "../Settings/Integrations/idibu.scss";
import "./idibuPostModal.scss";

/**
 * Job tab panel: loads authenticated HTML that auto-POSTs into the tenant's idibu iframe.
 * The idibu account itself is connected in Settings > Integration.
 */
function IdibuPostTab() {
  const { jobId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [frameUrl, setFrameUrl] = useState("");

  const loadFrame = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError("");
    setFrameUrl("");
    const result = await dispatch(fetchIdibuIframeHtml(jobId));
    if (result?.success && result.html) {
      setFrameUrl(URL.createObjectURL(new Blob([result.html], { type: "text/html" })));
    } else {
      setError(result?.message || "Failed to open Idibu posting");
    }
    setLoading(false);
  }, [dispatch, jobId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    const s = await dispatch(fetchIdibuStatus());
    setStatus(s || {});
    if (s?.effective_enabled) {
      await loadFrame();
    } else {
      setFrameUrl("");
      setLoading(false);
    }
  }, [dispatch, loadFrame]);

  useEffect(() => {
    if (jobId) refresh();
  }, [jobId, refresh]);

  useEffect(() => {
    return () => {
      if (frameUrl) URL.revokeObjectURL(frameUrl);
    };
  }, [frameUrl]);

  const canPost = Boolean(status?.effective_enabled);
  const canManage = Boolean(status?.can_manage);

  const renderNotReady = () => {
    const message = !status?.connected
      ? "idibu is not connected for your organization."
      : status?.reason || "You don't have access to post jobs to job boards. Ask your admin for access.";
    return (
      <div className="idibu-modal-state">
        <p className="idibu-muted" style={{ maxWidth: 480, fontSize: 14 }}>
          {message}
          {!status?.connected && !canManage ? " Ask your admin to connect it in Settings > Integration." : ""}
        </p>
        {canManage ? (
          <button type="button" className="idibu-btn-secondary" onClick={() => navigate("/integrations/idibu")}>
            Open idibu settings
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <div className="idibu-post-tab">
      <div className="idibu-post-tab-header">
        <div>
          <h3>Post to Job Boards</h3>
          <p>Select boards and publish this job inside Idibu.</p>
        </div>
        <div className="idibu-post-tab-actions">
          {error && canPost ? (
            <button type="button" className="idibu-btn-secondary" onClick={loadFrame}>
              Retry
            </button>
          ) : null}
        </div>
      </div>
      <div className="idibu-post-tab-body">
        {loading ? (
          <div className="idibu-modal-state">
            <div className="spinner-border" role="status" style={{ color: "#F75891" }}>
              <span className="visually-hidden">Loading…</span>
            </div>
            <p>Opening Idibu…</p>
          </div>
        ) : null}
        {!loading && status && !canPost ? renderNotReady() : null}
        {!loading && canPost && error ? (
          <div className="idibu-modal-state idibu-modal-error">
            <p>{error}</p>
          </div>
        ) : null}
        {!loading && canPost && !error && frameUrl ? (
          <iframe title="Idibu posting" className="idibu-frame" src={frameUrl} />
        ) : null}
      </div>
    </div>
  );
}

export default IdibuPostTab;
